<template>
  <div class="modal-overlay">
    <div class="modal-content p-4 rounded shadow bg-white">
      <!-- Wizard Header -->
      <div class="wizard-header mb-4">
        <h4>Create New Interaction - Step {{ currentStep }} of 6</h4>
        <div class="step-indicator mt-3">
          <div
            v-for="step in 6"
            :key="step"
            class="step-dot"
            :class="{
              'completed': step < currentStep,
              'active': step === currentStep,
              'pending': step > currentStep
            }">
            <span>{{ step }}</span>
          </div>
        </div>
      </div>

      <hr />

      <!-- Wizard Steps -->
      <div class="wizard-body" style="min-height: 400px">
        <!-- Step 1: Basic Info -->
        <WizardStep :stepNumber="1" title="Basic Information" :isActive="currentStep === 1">
          <h5 class="mb-3">Basic Information</h5>
          <div class="mb-3">
            <label class="form-label">Interaction Name <span class="text-danger">*</span></label>
            <input
              v-model="spec.name"
              type="text"
              class="form-control"
              placeholder="e.g., graphicChoice"
              @input="validateCamelCase"
              required />
            <small class="form-text text-muted">Must be in camelCase (e.g., myNewInteraction)</small>
            <div v-if="validationErrors.name" class="text-danger mt-1">
              {{ validationErrors.name }}
            </div>
          </div>

          <div class="mb-3">
            <label class="form-label">Display Name <span class="text-danger">*</span></label>
            <input
              v-model="spec.displayName"
              type="text"
              class="form-control"
              placeholder="e.g., Graphic Choice"
              required />
            <small class="form-text text-muted">Human-readable name shown in the UI</small>
          </div>

          <div class="mb-3">
            <label class="form-label">Author</label>
            <input
              v-model="spec.author"
              type="text"
              class="form-control"
              disabled />
          </div>

          <div class="mb-3">
            <label class="form-label">Description <span class="text-danger">*</span></label>
            <textarea
              v-model="spec.description"
              class="form-control"
              rows="3"
              maxlength="200"
              placeholder="Brief description of what this interaction does..."
              required></textarea>
            <small class="form-text text-muted">{{ spec.description.length }}/200 characters</small>
          </div>

          <div class="mb-3">
            <label class="form-label">Icon (60×60px) <span class="text-danger">*</span></label>
            <input
              type="file"
              accept="image/*"
              class="form-control"
              @change="handleIconUpload"
              required />
            <div v-if="iconPreview" class="mt-2">
              <img :src="iconPreview" alt="Icon preview" style="width: 60px; height: 60px; border: 1px solid #ddd; border-radius: 4px;" />
            </div>
          </div>
        </WizardStep>

        <!-- Step 2: Interaction Type -->
        <WizardStep :stepNumber="2" title="Interaction Type" :isActive="currentStep === 2">
          <h5 class="mb-3">Select Interaction Type</h5>
          <p class="text-muted">Choose the category that best describes your interaction</p>

          <div class="interaction-types">
            <div
              v-for="type in interactionTypes"
              :key="type.id"
              class="type-card"
              :class="{ 'selected': spec.type === type.id }"
              @click="selectType(type.id)">
              <div class="type-icon">{{ type.icon }}</div>
              <h6>{{ type.name }}</h6>
              <p>{{ type.description }}</p>
              <div v-if="type.examples" class="examples">
                <small><strong>Examples:</strong> {{ type.examples.join(', ') }}</small>
              </div>
            </div>
          </div>
        </WizardStep>

        <!-- Step 3: Features -->
        <WizardStep :stepNumber="3" title="Features" :isActive="currentStep === 3">
          <h5 class="mb-3">Configure Features</h5>
          <p class="text-muted">Select the features your interaction will support</p>

          <div class="features-grid">
            <!-- Scoring -->
            <div class="feature-group">
              <h6>Scoring</h6>
              <div class="form-check">
                <input
                  type="checkbox"
                  class="form-check-input"
                  id="feature-scoring"
                  v-model="spec.features.scoring"
                  checked
                  disabled />
                <label class="form-check-label" for="feature-scoring">
                  Enable Scoring (Required)
                </label>
              </div>
              <div v-if="spec.features.scoring" class="ms-4 mt-2">
                <label class="form-label">Scoring Type</label>
                <select v-model="spec.features.scoringType" class="form-select form-select-sm">
                  <option value="binary">Binary (all or nothing)</option>
                  <option value="partial">Partial Credit</option>
                  <option value="weighted">Weighted Options</option>
                  <option value="zone-based">Zone-Based (canvas)</option>
                </select>
              </div>
            </div>

            <!-- Feedback -->
            <div class="feature-group">
              <h6>Feedback</h6>
              <div class="form-check">
                <input
                  type="checkbox"
                  class="form-check-input"
                  id="feature-feedback"
                  v-model="spec.features.feedback" />
                <label class="form-check-label" for="feature-feedback">
                  Enable Feedback
                </label>
              </div>
              <div v-if="spec.features.feedback" class="ms-4 mt-2">
                <div class="form-check form-check-inline">
                  <input
                    type="checkbox"
                    class="form-check-input"
                    id="feedback-global"
                    v-model="spec.features.feedbackTypes.global" />
                  <label class="form-check-label" for="feedback-global">Global</label>
                </div>
                <div class="form-check form-check-inline">
                  <input
                    type="checkbox"
                    class="form-check-input"
                    id="feedback-option"
                    v-model="spec.features.feedbackTypes.perOption" />
                  <label class="form-check-label" for="feedback-option">Per Option</label>
                </div>
                <div class="form-check form-check-inline">
                  <input
                    type="checkbox"
                    class="form-check-input"
                    id="feedback-zone"
                    v-model="spec.features.feedbackTypes.perZone" />
                  <label class="form-check-label" for="feedback-zone">Per Zone</label>
                </div>
              </div>
            </div>

            <!-- Hint -->
            <div class="feature-group">
              <h6>Hint</h6>
              <div class="form-check">
                <input
                  type="checkbox"
                  class="form-check-input"
                  id="feature-hint"
                  v-model="spec.features.hint" />
                <label class="form-check-label" for="feature-hint">
                  Enable Hint Support
                </label>
              </div>
            </div>

            <!-- Randomization -->
            <div class="feature-group">
              <h6>Randomization</h6>
              <div class="form-check">
                <input
                  type="checkbox"
                  class="form-check-input"
                  id="feature-random"
                  v-model="spec.features.randomization" />
                <label class="form-check-label" for="feature-random">
                  Randomize Options Order
                </label>
              </div>
            </div>

            <!-- Canvas -->
            <div class="feature-group">
              <h6>Canvas</h6>
              <div class="form-check">
                <input
                  type="checkbox"
                  class="form-check-input"
                  id="feature-canvas"
                  v-model="spec.features.canvas"
                  @change="onCanvasToggle" />
                <label class="form-check-label" for="feature-canvas">
                  Enable Konva Canvas
                </label>
              </div>
              <div v-if="spec.features.canvas" class="ms-4 mt-2">
                <label class="form-label">Zone Types</label>
                <div class="form-check">
                  <input type="checkbox" class="form-check-input" id="zone-circle" v-model="spec.features.zoneTypes.circle" />
                  <label class="form-check-label" for="zone-circle">Circle</label>
                </div>
                <div class="form-check">
                  <input type="checkbox" class="form-check-input" id="zone-rect" v-model="spec.features.zoneTypes.rect" />
                  <label class="form-check-label" for="zone-rect">Rectangle</label>
                </div>
                <div class="form-check">
                  <input type="checkbox" class="form-check-input" id="zone-freeform" v-model="spec.features.zoneTypes.freeform" />
                  <label class="form-check-label" for="zone-freeform">Freeform (Polygon)</label>
                </div>
              </div>
            </div>

            <!-- Media -->
            <div class="feature-group">
              <h6>Media Support</h6>
              <div class="form-check">
                <input type="checkbox" class="form-check-input" id="media-image" v-model="spec.features.media.image" />
                <label class="form-check-label" for="media-image">Images</label>
              </div>
              <div class="form-check">
                <input type="checkbox" class="form-check-input" id="media-audio" v-model="spec.features.media.audio" />
                <label class="form-check-label" for="media-audio">Audio</label>
              </div>
              <div class="form-check">
                <input type="checkbox" class="form-check-input" id="media-video" v-model="spec.features.media.video" />
                <label class="form-check-label" for="media-video">Video</label>
              </div>
            </div>
          </div>
        </WizardStep>

        <!-- Step 4: Similar Template -->
        <WizardStep :stepNumber="4" title="Template" :isActive="currentStep === 4">
          <h5 class="mb-3">Choose a Similar Template</h5>
          <p class="text-muted">Select an existing interaction to use as a starting point</p>

          <div class="template-list">
            <div
              v-for="template in availableTemplates"
              :key="template.id"
              class="template-card"
              :class="{ 'selected': spec.templateBase === template.id }"
              @click="selectTemplate(template.id)">
              <div class="template-header">
                <h6>{{ template.name }}</h6>
                <span class="badge bg-secondary">{{ template.category }}</span>
              </div>
              <p class="template-desc">{{ template.description }}</p>
              <div class="template-features">
                <span v-for="feature in template.features" :key="feature" class="badge bg-light text-dark me-1">
                  {{ feature }}
                </span>
              </div>
            </div>
          </div>

          <div class="alert alert-info mt-3">
            <strong>Note:</strong> The template provides a structural starting point. You can customize all aspects in the next steps.
          </div>
        </WizardStep>

        <!-- Step 5: Advanced Configuration -->
        <WizardStep :stepNumber="5" title="Advanced Config" :isActive="currentStep === 5">
          <h5 class="mb-3">Advanced Configuration</h5>

          <div class="mb-4">
            <h6>Composables</h6>
            <p class="text-muted">Auto-detected based on selected features</p>
            <div class="composables-list">
              <div v-for="composable in detectedComposables" :key="composable" class="badge bg-primary me-2 mb-2">
                {{ composable }}
              </div>
            </div>
          </div>

          <div class="mb-4">
            <h6>Response Type</h6>
            <select v-model="spec.responseType" class="form-select">
              <option value="single">Single Response</option>
              <option value="multiple">Multiple Responses</option>
              <option value="text">Text Input</option>
              <option value="zones">Zone Clicks</option>
              <option value="custom">Custom</option>
            </select>
          </div>

          <div class="mb-4">
            <h6>Validation Rules</h6>
            <div class="form-check">
              <input
                type="checkbox"
                class="form-check-input"
                id="validation-required"
                v-model="spec.validation.required" />
              <label class="form-check-label" for="validation-required">
                Answer Required
              </label>
            </div>
            <div class="form-check">
              <input
                type="checkbox"
                class="form-check-input"
                id="validation-minlength"
                v-model="spec.validation.minLength" />
              <label class="form-check-label" for="validation-minlength">
                Minimum Length Validation
              </label>
            </div>
          </div>

          <div class="mb-4">
            <h6>Default Max Score</h6>
            <input
              type="number"
              v-model.number="spec.defaultMaxScore"
              class="form-control"
              min="0"
              step="0.5" />
          </div>
        </WizardStep>

        <!-- Step 6: Confirmation & Preview -->
        <WizardStep :stepNumber="6" title="Confirmation" :isActive="currentStep === 6">
          <h5 class="mb-3">Review & Confirm</h5>
          <p class="text-muted">Review the interaction specification before generating files</p>

          <div class="spec-preview">
            <h6>Interaction Summary</h6>
            <table class="table table-sm table-bordered">
              <tbody>
                <tr>
                  <th style="width: 30%">Name</th>
                  <td><code>{{ spec.name }}</code></td>
                </tr>
                <tr>
                  <th>Display Name</th>
                  <td>{{ spec.displayName }}</td>
                </tr>
                <tr>
                  <th>Type</th>
                  <td>{{ spec.type }}</td>
                </tr>
                <tr>
                  <th>Template Base</th>
                  <td>{{ spec.templateBase || 'None' }}</td>
                </tr>
                <tr>
                  <th>Scoring Type</th>
                  <td>{{ spec.features.scoringType }}</td>
                </tr>
                <tr>
                  <th>Features</th>
                  <td>
                    <span v-if="spec.features.feedback" class="badge bg-success me-1">Feedback</span>
                    <span v-if="spec.features.hint" class="badge bg-success me-1">Hint</span>
                    <span v-if="spec.features.canvas" class="badge bg-success me-1">Canvas</span>
                    <span v-if="spec.features.randomization" class="badge bg-success me-1">Randomization</span>
                  </td>
                </tr>
              </tbody>
            </table>

            <h6 class="mt-4">Files to be Generated</h6>
            <div class="file-tree">
              <div class="file-group">
                <strong>📁 waspleditor/src/components/interactions/{{ spec.name }}/</strong>
                <ul>
                  <li>✅ model.json</li>
                  <li>✅ Editor.vue</li>
                  <li>✅ Preview.vue</li>
                  <li v-if="spec.features.canvas">✅ ZoneDataTable.vue</li>
                </ul>
              </div>
              <div class="file-group mt-2">
                <strong>📁 waspltestrunner/src/</strong>
                <ul>
                  <li>✅ components/interactions/{{ spec.name }}Render.vue</li>
                  <li>✅ interactions/{{ spec.name }}Interaction.js</li>
                </ul>
              </div>
              <div class="file-group mt-2">
                <strong>📁 docs/interactions/</strong>
                <ul>
                  <li>✅ {{ spec.name }}-user-guide.md</li>
                </ul>
              </div>
            </div>

            <h6 class="mt-4">Options</h6>
            <div class="form-check mb-3">
              <input
                class="form-check-input"
                type="checkbox"
                id="forceOverwrite"
                v-model="spec.forceOverwrite">
              <label class="form-check-label" for="forceOverwrite">
                <strong>Force Overwrite</strong> - Replace existing interaction if it exists
                <small class="text-muted d-block">⚠️ This will overwrite all files for this interaction</small>
              </label>
            </div>

            <h6 class="mt-4">JSON Specification</h6>
            <div class="json-preview">
              <pre><code>{{ JSON.stringify(spec, null, 2) }}</code></pre>
            </div>
          </div>
        </WizardStep>
      </div>

      <!-- Wizard Footer -->
      <hr />
      <div class="wizard-footer d-flex justify-content-between">
        <button
          type="button"
          class="btn btn-secondary"
          @click="currentStep === 1 ? $emit('close') : previousStep()">
          {{ currentStep === 1 ? 'Cancel' : 'Previous' }}
        </button>

        <div>
          <button
            v-if="currentStep < 6"
            type="button"
            class="btn btn-primary"
            @click="nextStep"
            :disabled="!canProceed">
            Next
          </button>
          <button
            v-else
            type="button"
            class="btn btn-success"
            @click="generateInteraction"
            :disabled="isGenerating">
            {{ isGenerating ? 'Generating...' : 'Generate Interaction' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import { useUserStore } from '@/stores/userStore'
import WizardStep from './WizardStep.vue'

const emit = defineEmits(['close', 'created'])

// Wizard state
const currentStep = ref(1)
const isGenerating = ref(false)
const iconPreview = ref(null)
const validationErrors = ref({})

// User store
const userStore = useUserStore()

// Interaction specification
const spec = ref({
  name: '',
  displayName: '',
  author: '',
  description: '',
  icon: null,
  type: 'choice-based',
  templateBase: null,
  features: {
    scoring: true,
    scoringType: 'binary',
    feedback: true,
    feedbackTypes: {
      global: true,
      perOption: false,
      perZone: false
    },
    hint: false,
    randomization: false,
    canvas: false,
    zoneTypes: {
      circle: false,
      rect: false,
      freeform: false
    },
    media: {
      image: false,
      audio: false,
      video: false
    }
  },
  responseType: 'single',
  validation: {
    required: true,
    minLength: false
  },
  defaultMaxScore: 1,
  forceOverwrite: false,
  createdAt: Date.now()
})

// Interaction types
const interactionTypes = ref([
  {
    id: 'choice-based',
    name: 'Choice-Based',
    icon: '☑️',
    description: 'Interactions with discrete options to select (radio/checkbox)',
    examples: ['choice', 'choiceBlock']
  },
  {
    id: 'canvas-based',
    name: 'Canvas-Based',
    icon: '🎨',
    description: 'Visual interactions using Konva canvas with zones',
    examples: ['hotSpot']
  },
  {
    id: 'hybrid-choice-canvas',
    name: 'Hybrid (Choice + Canvas)',
    icon: '🎯',
    description: 'Combines clickable zones on canvas with choice logic',
    examples: ['graphicChoice']
  },
  {
    id: 'text-input',
    name: 'Text Input',
    icon: '✍️',
    description: 'Requires text input from users with pattern matching',
    examples: ['shortAnswer', 'textGaps']
  },
  {
    id: 'custom',
    name: 'Custom',
    icon: '⚙️',
    description: 'Start from minimal template for unique interactions',
    examples: []
  }
])

// Available templates
const availableTemplates = ref([
  {
    id: 'choice',
    name: 'Choice',
    category: 'choice-based',
    description: 'Single or multiple choice with text options',
    features: ['Options', 'Feedback', 'Hint', 'Randomization']
  },
  {
    id: 'hotSpot',
    name: 'Hot Spot',
    category: 'canvas-based',
    description: 'Drag stickers onto zones on an image',
    features: ['Canvas', 'Zones', 'Image Upload', 'Stickers']
  },
  {
    id: 'graphicChoice',
    name: 'Graphic Choice',
    category: 'hybrid-choice-canvas',
    description: 'Click on zones within an image (choice + canvas)',
    features: ['Canvas', 'Zones', 'Image Upload', 'Feedback', 'Hint', 'Single/Multiple']
  },
  {
    id: 'shortAnswer',
    name: 'Short Answer',
    category: 'text-input',
    description: 'Text input with pattern matching or AI validation',
    features: ['Text Input', 'Validation', 'AI Correction']
  },
  {
    id: 'message',
    name: 'Message',
    category: 'basic',
    description: 'Simple informational message (no scoring)',
    features: ['Rich Text Only']
  }
])

// Computed
const detectedComposables = computed(() => {
  const composables = ['useQuillEditor']

  if (spec.value.features.canvas) {
    composables.push('useKonvaCanvas', 'useZoneManager', 'useImageProcessing')
  }

  if (spec.value.type === 'choice-based' || spec.value.type === 'hybrid-choice-canvas') {
    composables.push('useOptionManager')
  }

  if (spec.value.features.feedback || spec.value.features.hint) {
    composables.push('useFeedbackManager')
  }

  composables.push('useScoring')

  return composables
})

const canProceed = computed(() => {
  switch (currentStep.value) {
    case 1:
      return spec.value.name &&
             spec.value.displayName &&
             spec.value.description &&
             spec.value.icon &&
             !validationErrors.value.name
    case 2:
      return spec.value.type !== null
    case 3:
      return true // Features are optional
    case 4:
      return true // Template is optional
    case 5:
      return true // Advanced config is optional
    case 6:
      return true
    default:
      return false
  }
})

// Methods
const validateCamelCase = () => {
  const camelCaseRegex = /^[a-z][a-zA-Z0-9]*$/
  if (!spec.value.name) {
    validationErrors.value.name = 'Name is required'
  } else if (!camelCaseRegex.test(spec.value.name)) {
    validationErrors.value.name = 'Name must be in camelCase (e.g., myInteraction)'
  } else {
    delete validationErrors.value.name
  }
}

const handleIconUpload = (event) => {
  const file = event.target.files[0]
  if (file) {
    spec.value.icon = file
    const reader = new FileReader()
    reader.onload = (e) => {
      iconPreview.value = e.target.result
    }
    reader.readAsDataURL(file)
  }
}

const selectType = (typeId) => {
  spec.value.type = typeId

  // Auto-configure features based on type
  if (typeId === 'canvas-based' || typeId === 'hybrid-choice-canvas') {
    spec.value.features.canvas = true
    spec.value.features.zoneTypes.circle = true
    spec.value.features.zoneTypes.rect = true
    spec.value.features.zoneTypes.freeform = true
    spec.value.features.scoringType = 'zone-based'
  }

  if (typeId === 'choice-based' || typeId === 'hybrid-choice-canvas') {
    spec.value.features.feedback = true
    spec.value.features.hint = true
  }
}

const onCanvasToggle = () => {
  if (!spec.value.features.canvas) {
    spec.value.features.zoneTypes = {
      circle: false,
      rect: false,
      freeform: false
    }
  }
}

const selectTemplate = (templateId) => {
  spec.value.templateBase = templateId
}

const nextStep = () => {
  if (canProceed.value && currentStep.value < 6) {
    currentStep.value++
  }
}

const previousStep = () => {
  if (currentStep.value > 1) {
    currentStep.value--
  }
}

const generateInteraction = async () => {
  isGenerating.value = true

  try {
    // Use local editor API (port 4000) instead of testrunner
    const apiBase = 'http://localhost:4000'
    const systemToken = import.meta.env.VITE_SYSTEM_TOKEN_FOR_TESTRUNNER

    // Convert icon to base64 if needed
    let iconBase64 = null
    if (spec.value.icon) {
      iconBase64 = await fileToBase64(spec.value.icon)
    }

    const payload = {
      ...spec.value,
      icon: iconBase64
    }

    const res = await fetch(`${apiBase}/api/generateInteraction`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${systemToken}`
      },
      body: JSON.stringify(payload)
    })

    const result = await res.json()

    if (!res.ok) {
      throw new Error(result?.error || 'Unknown server error')
    }

    console.log('✅ Interaction generated:', result.message)

    emit('created', {
      interactionName: spec.value.name,
      spec: spec.value,
      files: result.files
    })

    // Close modal
    emit('close')
  } catch (error) {
    console.error('❌ Error generating interaction:', error.message)
    alert('Error: ' + error.message)
  } finally {
    isGenerating.value = false
  }
}

const fileToBase64 = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(reader.result)
    reader.onerror = reject
    reader.readAsDataURL(file)
  })
}

// Lifecycle
onMounted(() => {
  spec.value.author = userStore.user?.username || 'unknown'
})
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  overflow-y: auto;
  padding: 2rem 0;
}

.modal-content {
  max-width: 800px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  background: white;
}

/* Step Indicator */
.step-indicator {
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: relative;
}

.step-indicator::before {
  content: '';
  position: absolute;
  top: 50%;
  left: 0;
  right: 0;
  height: 2px;
  background: #ddd;
  z-index: 0;
}

.step-dot {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: #f0f0f0;
  border: 2px solid #ddd;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  color: #999;
  position: relative;
  z-index: 1;
  transition: all 0.3s ease;
}

.step-dot.completed {
  background: #28a745;
  border-color: #28a745;
  color: white;
}

.step-dot.active {
  background: #007bff;
  border-color: #007bff;
  color: white;
  transform: scale(1.1);
}

.step-dot.pending {
  background: white;
  border-color: #ddd;
  color: #999;
}

/* Interaction Types */
.interaction-types {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 1rem;
}

.type-card {
  border: 2px solid #ddd;
  border-radius: 8px;
  padding: 1rem;
  cursor: pointer;
  transition: all 0.3s ease;
  text-align: center;
}

.type-card:hover {
  border-color: #007bff;
  box-shadow: 0 4px 8px rgba(0, 123, 255, 0.2);
}

.type-card.selected {
  border-color: #007bff;
  background-color: #e7f3ff;
}

.type-icon {
  font-size: 3rem;
  margin-bottom: 0.5rem;
}

.type-card h6 {
  font-size: 1rem;
  font-weight: bold;
  margin-bottom: 0.5rem;
}

.type-card p {
  font-size: 0.875rem;
  color: #666;
  margin-bottom: 0.5rem;
}

.examples {
  font-size: 0.75rem;
  color: #888;
}

/* Features Grid */
.features-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: 1.5rem;
}

.feature-group {
  padding: 1rem;
  border: 1px solid #ddd;
  border-radius: 8px;
  background: #f9f9f9;
}

.feature-group h6 {
  font-size: 0.95rem;
  font-weight: bold;
  margin-bottom: 0.75rem;
  color: #333;
}

/* Templates */
.template-list {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.template-card {
  border: 2px solid #ddd;
  border-radius: 8px;
  padding: 1rem;
  cursor: pointer;
  transition: all 0.3s ease;
}

.template-card:hover {
  border-color: #007bff;
  box-shadow: 0 4px 8px rgba(0, 123, 255, 0.2);
}

.template-card.selected {
  border-color: #007bff;
  background-color: #e7f3ff;
}

.template-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.5rem;
}

.template-desc {
  font-size: 0.875rem;
  color: #666;
  margin-bottom: 0.5rem;
}

.template-features {
  display: flex;
  flex-wrap: wrap;
  gap: 0.25rem;
}

/* Composables */
.composables-list {
  display: flex;
  flex-wrap: wrap;
}

/* Spec Preview */
.spec-preview {
  background: #f9f9f9;
  padding: 1rem;
  border-radius: 8px;
}

.file-tree {
  background: white;
  padding: 1rem;
  border-radius: 4px;
  border: 1px solid #ddd;
}

.file-group ul {
  list-style: none;
  padding-left: 1.5rem;
  margin: 0.5rem 0;
}

.file-group li {
  padding: 0.25rem 0;
  font-family: monospace;
}

.json-preview {
  background: #282c34;
  color: #abb2bf;
  padding: 1rem;
  border-radius: 4px;
  overflow-x: auto;
  max-height: 300px;
  font-family: 'Courier New', monospace;
  font-size: 0.875rem;
}

.json-preview pre {
  margin: 0;
}
</style>
